import { useCallback, useEffect } from 'react';

import { snackbar } from '@/_shared';

import { FilterTypeSelector } from '../filter-type-selector';
import { FilterApplyTrigger } from '../filters-apply-trigger';
import { getFilterKey, useFiltersState } from '../helper';
import type { AdvanceFilter } from '../types';

export function RadioOnlyFilters<T>({
  handleSubmitAdvFilters,
  ...perFilter
}: AdvanceFilter<T> & {
  handleSubmitAdvFilters: (v: AdvanceFilter<T>) => void;
}) {
  const { mainKey, subKey, subKey2, types, selectedType, applied } = perFilter;
  const {
    typeState: [type, setType],
    transition: [isPending, startTransition],
  } = useFiltersState(null as string | null, types, selectedType);

  const handleSubmit = useCallback(() => {
    if (type === null || typeof type === 'undefined' || type === '') {
      snackbar.error('Please select a filter type');
      return;
    }

    const selectedType = types.find((f) => f.value === type) ?? { name: '', value: '' };

    const key = getFilterKey(mainKey as string, subKey, subKey2, selectedType?.absoluteKey);
    let valueToApply = `${type}[${key}]`;
    if ((type === 'match' || type === 'not_match') && selectedType?.optionValue) {
      valueToApply = `${type}[${key}]=${selectedType.optionValue}`;
    }

    // Handle specific cases for 'under' and 'over' for inventory stock filters
    if ((type === 'under' || type === 'over') && selectedType?.optionValue) {
      valueToApply = `${selectedType.optionValue}[${key}]=${type}`;
    }

    startTransition(() => {
      handleSubmitAdvFilters({
        ...perFilter,
        applied: true,
        valueToApply,
        filterValue: type,
        selectedType,
      });
    });
  }, [type, mainKey, subKey, subKey2, startTransition, handleSubmitAdvFilters, perFilter, types]);

  useEffect(() => {
    if (!applied) {
      setType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  return (
    <section className="flex flex-col">
      <FilterTypeSelector types={types} type={type} setType={setType} />

      <FilterApplyTrigger handleSubmit={handleSubmit} isPending={isPending} />
    </section>
  );
}
